// Globals
import { useTranslation } from 'react-i18next';

// Modules
import { ImagesUploaderContainer } from '@/modules/Upload';

// Components
import { Button, Spinner } from '@/ui';
import ChangeOrderStatus from '../ChangeOrderStatus/ChangeOrderStatus';
import OrderDetails from '../OrderDetails/OrderDetails';
import { StyledContainer } from './PhotoshootingDetailsStyles';

// Models
import { FileVariant, IFile } from '@/models/file.model';
import { IOrder, OrderStatus } from '@/models/order.model';

// Helpers
import { ACCEPT_IMAGE_FILE_TYPES, MAX_IMAGE_SIZE } from '../../helpers';

type TPhotoshootingDetailsProps = {
  order: IOrder;
  isLoading: boolean;
  onUploadFile: (filesList: File[], type: FileVariant) => void;
  onDelete: (file: IFile) => void;
  onChangeStatus: (status: OrderStatus) => void;
};

const PhotoshootingDetails = ({
  isLoading,
  order,
  onChangeStatus,
  onDelete,
  onUploadFile,
}: TPhotoshootingDetailsProps) => {
  const { t } = useTranslation('order');
  const handleUpload = (type: FileVariant) => (_file: File, filesList: File[]) => onUploadFile(filesList, type);

  return (
    <Spinner spinning={isLoading}>
      <StyledContainer>
        <OrderDetails order={order} />
        <ChangeOrderStatus value={order.status} loading={isLoading} onChange={onChangeStatus} />
        <ImagesUploaderContainer
          acceptance={ACCEPT_IMAGE_FILE_TYPES}
          onChange={handleUpload(FileVariant.PHOTO_INSIDE_PROPERTY)}
          title={(order.status === OrderStatus.IN_PROGRESS || !!order.photos_inside?.length) && t('label_inside')}
          description={t('select_photos_label')}
          disabled={order.status !== OrderStatus.IN_PROGRESS}
          imagesList={order?.photos_inside}
          maxImageSize={MAX_IMAGE_SIZE}
          multiple
          onDelete={onDelete}
        >
          <Button shape="round" type="primary">
            {t('select_photos', { ns: 'common' })}
          </Button>
        </ImagesUploaderContainer>
        <ImagesUploaderContainer
          acceptance={ACCEPT_IMAGE_FILE_TYPES}
          onChange={handleUpload(FileVariant.PHOTO_OUTSIDE_PROPERTY)}
          title={(order.status === OrderStatus.IN_PROGRESS || !!order.photos_outside?.length) && t('label_outside')}
          description={t('select_photos_label')}
          disabled={order.status !== OrderStatus.IN_PROGRESS}
          imagesList={order?.photos_outside}
          maxImageSize={MAX_IMAGE_SIZE}
          multiple
          onDelete={onDelete}
        >
          <Button shape="round" type="primary">
            {t('select_photos', { ns: 'common' })}
          </Button>
        </ImagesUploaderContainer>
      </StyledContainer>
    </Spinner>
  );
};

export default PhotoshootingDetails;
